import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { fetchLogin } from "../../services/api";
import { useAuth } from "../../contexts/authContext";
import { useLanguage } from "../../contexts/LanguageContext";
import { translations } from "./translations";

const LoginPage = () => {
  const [loginMethod, setLoginMethod] = useState("email"); // 'email' или 'libraryCard'
  const [email, setEmail] = useState("");
  const [libraryCard, setLibraryCard] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [userInfo, setUserInfo] = useState(null);

  const { login } = useAuth();
  const navigate = useNavigate();
  const { currentLanguage } = useLanguage();
  const t = translations[currentLanguage] || translations.kz;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const data = await fetchLogin(
        loginMethod === "email" ? email : null,
        password,
        loginMethod === "libraryCard" ? libraryCard : null
      );

      if (data && data.token) {
        if (data.admin) {
          login(data.token, data.admin);
          localStorage.setItem("token", data.token);
          localStorage.setItem("role", data.admin.role);
          localStorage.setItem("adminId", data.admin.id);
          localStorage.setItem("name", data.admin.name);
          localStorage.setItem("libraryCard", data.admin.libraryCard);

          navigate("/admin");
        } else if (data.user) {
          login(data.token, data.user);
          localStorage.setItem("token", data.token);
          localStorage.setItem("role", data.user.role);
          localStorage.setItem("userId", data.user.id);
          localStorage.setItem("firstName", data.user.firstName);
          localStorage.setItem("lastName", data.user.lastName);
          localStorage.setItem("libraryCard", data.user.libraryCard);
          localStorage.setItem("avatar_url", data.user.avatar_url);

          setUserInfo({
            fullName: `${data.user.lastName} ${data.user.firstName} ${
              data.user.middleName || ""
            }`.trim(),
            libraryCard: data.user.libraryCard,
          });
          setShowSuccessModal(true);
        }
      } else {
        throw new Error("Invalid response structure");
      }
    } catch (error) {
      console.error("Login failed:", error);
      setError(t.login.errors.invalid);
    } finally {
      setLoading(false);
    }
  };

  const closeModal = () => {
    setShowSuccessModal(false);
    navigate("/");
  };

  return (
    <div
      className="bg-gray-50 flex items-center justify-center px-4 py-8 sm:px-6 lg:px-8"
      style={{ minHeight: "calc(100vh - 80px)" }}
    >
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <h2 className="text-2xl sm:text-3xl font-extrabold text-gray-900">
            {t.login.title}
          </h2>
          <p className="mt-2 text-sm text-gray-600">{t.login.subtitle}</p>
        </div>

        <form
          className="bg-white p-6 sm:p-8 rounded-lg shadow-md space-y-6"
          onSubmit={handleSubmit}
        >
          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
              {error}
            </div>
          )}

          {/* Переключение метода входа */}
          <div>
            <div className="flex space-x-2 sm:space-x-4 justify-center">
              <button
                type="button"
                onClick={() => setLoginMethod("email")}
                className={`px-3 py-2 sm:px-4 rounded-md text-xs sm:text-sm font-medium transition-colors ${
                  loginMethod === "email"
                    ? "bg-[var(--primary-color)] text-white"
                    : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                }`}
              >
                {t.login.methods.email}
              </button>
              <button
                type="button"
                onClick={() => setLoginMethod("libraryCard")}
                className={`px-3 py-2 sm:px-4 rounded-md text-xs sm:text-sm font-medium transition-colors ${
                  loginMethod === "libraryCard"
                    ? "bg-[var(--primary-color)] text-white"
                    : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                }`}
              >
                {t.login.methods.libraryCard}
              </button>
            </div>
          </div>

          <div className="space-y-4">
            {/* Email или читательский билет */}
            {loginMethod === "email" ? (
              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  {t.login.form.email}
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={loading}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)] focus:border-[var(--primary-color)]"
                  placeholder={t.login.placeholders.email}
                />
              </div>
            ) : (
              <div>
                <label
                  htmlFor="libraryCard"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  {t.login.form.libraryCard}
                </label>
                <input
                  id="libraryCard"
                  name="libraryCard"
                  type="text"
                  maxLength="6"
                  pattern="[0-9]{6}"
                  required
                  value={libraryCard}
                  onChange={(e) => setLibraryCard(e.target.value)}
                  disabled={loading}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)] focus:border-[var(--primary-color)]"
                  placeholder={t.login.placeholders.libraryCard}
                />
                <p className="mt-1 text-xs text-gray-500">
                  {t.login.hints.libraryCard}
                </p>
              </div>
            )}

            {/* Пароль */}
            <div>
              <label
                htmlFor="password"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                {t.login.form.password}
              </label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={loading}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)] focus:border-[var(--primary-color)]"
                placeholder={t.login.placeholders.password}
              />
            </div>
          </div>

          <div>
            <button
              type="submit"
              disabled={loading}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-[var(--primary-color)] hover:bg-white hover:text-black hover:border-[var(--primary-color)] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[var(--primary-color)] disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
            >
              {loading ? t.login.buttons.signingIn : t.login.buttons.signIn}
            </button>
          </div>

          <div className="text-center">
            <p className="text-sm text-gray-600">
              {t.login.footer.noAccount}{" "}
              <a
                href="/register"
                className="font-medium text-[var(--primary-color)] hover:text-[var(--primary-color)]"
              >
                {t.login.footer.register}
              </a>
            </p>
          </div>
        </form>
      </div>

      {/* Модальное окно успешного входа для пользователей */}
      {showSuccessModal && userInfo && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full flex items-center justify-center z-50 p-4">
          <div className="bg-white w-full max-w-sm sm:max-w-md mx-auto rounded-lg shadow-lg p-6 sm:p-8">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
                <svg
                  className="h-6 w-6 text-green-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>

              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {t.login.success.title}
              </h3>

              <div className="text-center space-y-2 mb-6">
                <p className="text-base sm:text-lg font-semibold text-gray-800 break-words">
                  {userInfo.fullName}
                </p>
                <p className="text-sm text-gray-600">
                  {t.login.success.libraryCard}{" "}
                  <span className="font-mono font-bold text-[var(--primary-color)]">
                    {userInfo.libraryCard}
                  </span>
                </p>
              </div>

              <button
                onClick={closeModal}
                className="w-full inline-flex justify-center px-4 py-2 bg-[var(--primary-color)] border border-transparent rounded-md shadow-sm text-base font-medium text-white hover:bg-white hover:text-black hover:border-[var(--primary-color)] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[var(--primary-color)] transition-all duration-200"
              >
                {t.common.continue}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LoginPage;
